import { ChangeDetectorRef, Component, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import {
    ColumnDef,
    SortingState,
    Table,
    createAngularTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel
} from '@tanstack/angular-table';
import moment from 'moment';
import { forkJoin, map } from 'rxjs';
import { Settings } from "../../../account/models";
import { SettingService, UserSettingService } from "../../../account/services";
import { User } from "../../../auth/models";
import { UserSettings } from "../../../auth/models/user/user-settings";
import { AuthService } from "../../../auth/services";
import { CurrencyHelper } from "../../../common/helpers";
import { UnitHelper } from "../../../common/helpers/unit/unit.helper";
import { CurrencyRatesStruct } from "../../../common/models/currency-rates/currency-rates";
import { CurrencyRatesService, UnitRatesService } from "../../../common/services";
import { BidRateTables, formulaWeightToVolume, loadBidRateTables, tableUsgPerMt } from "../../../common/helpers/unit/bid-unit-rates";
import { indexRateKey } from "../../../indexes/services/platts/platts-prices.service";
import { FormulaConfig, FormulaParserService } from "../../../common/services/formula-parser.service";
import { PlattsPricesService } from "../../../indexes/services";
import { ToastService } from "../../../main/services";
import { Contract, ConversionFactor, Tender } from "../../models";
import { Offer } from "../../models/offer";
import { TenderRevisionService } from "../../services";
import { HeaderDef, overviewHeaders } from './tender-overview.headers';


// Columns the user can freeze on the left side of the table.
// Order in this array is the visual left-to-right order of frozen columns.
const FREEZABLE_COLUMN_ORDER = [
    'iata',
    'supplier',
    'location_company',
    'yearly_volume',
    'monthly_volume',
    'fuel_type',
    'flight_type',
    'delivery_point',
    'round',
    'offer',
    'status',
];

// Map of company-settings field key → table column id for columns
// that are conditional on company settings.
const SETTINGS_GATED_COLUMNS: { settingsKey: string; columnId: string }[] = [
    { settingsKey: 'location_fuel_type', columnId: 'fuel_type' },
    { settingsKey: 'location_flight_type', columnId: 'flight_type' },
    { settingsKey: 'location_delivery_point', columnId: 'delivery_point' },
];

@Component({
    selector: 'jft-tender-overview',
    templateUrl: './tender-overview.component.html',
    styleUrls: ['./tender-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderOverviewComponent implements OnInit {
    public defaultDateFormat: string = 'dd MMM yyyy';
    public companySettings: Settings;
    public selectedModelTab;
    public loading: boolean = false;
    public offers: Offer[] = [];
    public tender: Tender;
    public previousTender: Tender;
    public contract: Contract;
    public conversionFactor: ConversionFactor;
    public includedIndex: boolean = false;
    public includedMarket: boolean = false;
    private indexToggleHasUserChoice = false;
    private marketToggleHasUserChoice = false;
    public hideParameters = true;
    public indexDateRange: string = '12_month';
    public indexDateStart: string = '';
    public indexDateEnd: string = '';
    public currencies: {} = {};
    public defaultUnitRates;
    public customUnits = {};
    // Per-bid tables (decisions #46, #47); a part at the tender's rate uses customUnits (the modal's edits).
    public rateTables: BidRateTables | null = null;
    public baseCurrency: CurrencyRatesStruct = {};
    public baseUnit: CurrencyRatesStruct = {};
    public finalBaseAmount: number = 0;
    public finalGap: number = 0;
    public finalGapTotal: number = 0;
    public currentContractsTotal: number = 0;
    public totalVolume: number = 0;
    public selectedContractsTotal: number = 0;
    public selectedTotalVolume: number = 0;
    public hasSelectedBids: boolean = false;
    public selected: [] = [];
    public isInit: boolean = true;
    public isPrevOffersExists = false;
    public userSettings: UserSettings;
    public isConvertedUnits = true;
    public search: string = '';
    public isFullscreen: boolean = false;
    public pageSizeOptions: number[] = [10, 25, 50, 100];
    public table: Table<Offer>;
    public sorting: SortingState = [];

    public headers: HeaderDef[] = overviewHeaders();

    public orderedHeaders: Record<string, number> = {};
    public orderedHeadersArray: string[] = [];
    public colHeaders: Record<string, string> = {};
    public hiddenHeaders: string[] = [];
    public frozenColumns: string[] = [];

    private activeFormulas: Record<string, FormulaConfig> = {};
    private columnDefs: ColumnDef<Offer>[] = [];
    private columnWidths: Record<string, number> = {};
    private settingsHiddenColumnIds = new Set<string>();

    constructor(
        private route: ActivatedRoute,
        protected toast: ToastService,
        private router: Router,
        private service: TenderRevisionService,
        private plattsPricesService: PlattsPricesService,
        private currencyRatesService: CurrencyRatesService,
        private settingsService: SettingService,
        private userSettingService: UserSettingService,
        private authService: AuthService,
        private formulaParser: FormulaParserService,
        private cdr: ChangeDetectorRef,
        public user: User,
        private unitRatesService: UnitRatesService,
    ) {
    }

    public ngOnInit(): void {
        const data = this.route.snapshot.data;
        const baseUnit = this.getStorageItem('base_unit');
        const baseCurr = this.getStorageItem('base_curr');

        this.defaultUnitRates = data['unitRates'];
        this.conversionFactor = data['conversion_factor'];
        this.tender = data['tender'];
        this.userSettings = data['userSettings'];

        const user = this.authService.getCurrentUser();
        const customFields = user?.company?.settings?.custom_fields;
        this.activeFormulas = FormulaParserService.getActiveFormulas(
            customFields?.formulas,
            customFields?.deleted_formulas,
        );
        // Append a column for each active formula (configured under
        // /system-settings/formulas). Values are computed during recalc
        // and stored on bid[formulaKey].
        let formulaIndex = 200;
        const existingHeaderIds = new Set(this.headers.map(h => h.id));
        for (const [key, cfg] of Object.entries(this.activeFormulas)) {
            if (existingHeaderIds.has(key)) continue;
            this.headers.push({
                formId: key,
                id: key,
                label: cfg.name || key,
                order: true,
                index: formulaIndex++,
            });
        }

        if (this.userSettings?.revis_col_hide) {
            this.hiddenHeaders = this.userSettings.revis_col_hide;
        }
        if (this.userSettings?.revis_col_sort) {
            this.sorting = this.userSettings.revis_col_sort || [];
        }
        this.frozenColumns = (this.userSettings?.revis_col_freeze && this.userSettings.revis_col_freeze.length)
            ? this.userSettings.revis_col_freeze
            : ['iata', 'supplier'];

        // Filter out columns that are hidden by company settings (fuel_type / flight_type / delivery_point)
        const companySettings = this.tender?.company?.settings;
        this.settingsHiddenColumnIds = new Set<string>();
        if (companySettings && typeof companySettings.isHiddenField === 'function') {
            for (const { settingsKey, columnId } of SETTINGS_GATED_COLUMNS) {
                if (companySettings.isHiddenField(settingsKey, undefined, 'airline')) {
                    this.settingsHiddenColumnIds.add(columnId);
                }
            }
        } else {
            // No settings available — keep all gated columns hidden by default to avoid surprising rows.
            for (const { columnId } of SETTINGS_GATED_COLUMNS) {
                this.settingsHiddenColumnIds.add(columnId);
            }
        }
        this.headers = this.headers.filter(h => !this.settingsHiddenColumnIds.has(h.id));
        this.frozenColumns = this.frozenColumns.filter(id => !this.settingsHiddenColumnIds.has(id));
        this.columnWidths = {};
        for (const h of this.headers) {
            if (h.width) this.columnWidths[h.id] = h.width;
        }
        this.settingsService.fetch().subscribe((settings) => {
            this.companySettings = settings;
            this.defaultDateFormat = this.companySettings.date_format == 'MM-DD-YYYY' ? 'MM-dd-yyyy' : 'dd MMM yyyy';
        });
        // Read stored toggles; null means "no choice yet" — leave undecided
        // and apply defaults after offers load (default-on if data exists).
        const storedIndex = this.getStorageItem('included_index');
        const storedMarket = this.getStorageItem('included_market');
        this.includedIndex = storedIndex === null ? false : storedIndex;
        this.includedMarket = storedMarket === null ? false : storedMarket;
        this.indexToggleHasUserChoice = storedIndex !== null;
        this.marketToggleHasUserChoice = storedMarket !== null;
        if (this.isConvertedUnits) {
            this.baseCurrency = this.conversionFactor.currency;
            this.baseUnit = this.conversionFactor.units;
        } else {
            this.baseCurrency = baseCurr ? baseCurr : this.conversionFactor.currency;
            this.baseUnit = baseUnit ? baseUnit : this.conversionFactor.units;
        }

        this.init();
    }

    private init() {
        this.setOrderedHeaders();
        this.setColumnHeaders();
        this.setCustomUnits();
        this.fetchOffers();
    }

    private fetchOffers() {
        this.loading = true;

        // Critical path: offers + currency rates. Render the table as
        // soon as both arrive so the user sees data fast.
        const offersObs = this.service.fetchAll(this.tender.id, this.search).pipe(
            map((offers: any[]) => {
                const filtered = this.filterOrphanCurrentContracts(offers);
                this.offers = this.sortOffersByLocation(filtered);
                this.setOffersCurrencies();
            })
        );
        const ratesObs = this.getCurrencyRates();

        forkJoin([offersObs, ratesObs]).subscribe(() => {
            this.loading = false;
            this.applyIncludeDefaultsFromData();
            this.recalculateAll();
            this.buildColumnDefs();
            this.createTable();
            this.cdr.detectChanges();

            // Non-critical work runs after the table has painted so the
            // initial render isn't blocked on any of these.
            //   1) Previous tender (only used inside the conversion-params
            //      modal "Tender Comparison" tab)
            //   2) Index average price refresh (Platts/Argus/Opis prices
            //      for the default 12-month window — values are 0 until
            //      this completes, then the table re-renders)
            const tenderCurrentContractObs = this.fetchTenderCurrentContract();
            if (tenderCurrentContractObs) {
                tenderCurrentContractObs.subscribe();
            }
            this.refreshIndexAveragePrices();
            this.loadRateTables();
        });
    }

    /** Loads a table for every rate a bid needs beyond the tender's, then reprices. */
    private loadRateTables() {
        loadBidRateTables(this.unitRatesService, this.defaultUnitRates, (this.offers || []).map((o: any) => o?.bid))
            .subscribe(tables => {
                this.rateTables = tables;
                this.recalculateAll();
                this.refreshTable();
                this.cdr.detectChanges();
            });
    }

    /** The tables one bid converts with: its own rate's, else customUnits (the tender's, as edited). */
    /** The SAF fee (saf_fee_amount_units) in base currency and unit at the bid's SAF fee rate,
     * as bid_total._saf: it enters the total and the formulas' saf_fee_amount. */
    public safFeeValueOf(bid: any, table: any): number {
        const fee = bid?.['saf_fee_amount_units'];
        if (!fee?.price?.value || !fee.price.currency?.code || !fee.units?.code) {
            return 0;
        }
        return this.toCurrencyToUnit(fee.price.currency.code, fee.units.code, fee.price.value, table) || 0;
    }

    public tablesOf(bid: any): { jet: any; saf: any; safFee: any } {
        if (!this.rateTables) {
            return { jet: this.customUnits, saf: this.customUnits, safFee: this.customUnits };
        }
        const parts = this.rateTables.ofBid(bid);
        const own = (table: any) => (table === this.rateTables.tenderTable ? this.customUnits : table);
        return { jet: own(parts.jet), saf: own(parts.saf), safFee: own(parts.safFee) };
    }

    /**
     * Refresh index average prices for the current date range. Mirrors the
     * fetch pattern used by /tenders/.../dashboard/analysis: deduplicates
     * API calls per (indexId, bateCode) tuple so the same Average Price
     * Base is computed regardless of which page surfaces it.
     */
    private refreshIndexAveragePrices() {
        const params = {
            'currencyId': this.baseCurrency.id,
            'unitId': this.baseUnit.id,
            'dateRange': this.indexDateRange,
            'startDate': this.indexDateStart,
            'endDate': this.indexDateEnd,
            'tenderId': this.tender.id,
        };

        const uniqueKeys = new Map<string, { indexId: any; bateCode: any; wToVolume: string }>();
        const offerKeyMap: { offer: any; key: string }[] = [];

        for (const offer of this.offers as any[]) {
            const idx = offer?.bid?.index;
            const provider = idx?.index_code?.provider;
            if (idx && (provider === 'platts' || provider === 'argus' || provider === 'opis')) {
                const indexId = idx.index_code.id;
                const bateCode = idx.bate ? idx.bate.id : '';
                const wToVolume = indexRateKey(offer.bid);  // an MT index averages differently per bid rate
                const key = `${indexId}_${bateCode}_${wToVolume}`;
                uniqueKeys.set(key, { indexId, bateCode, wToVolume });
                offerKeyMap.push({ offer, key });
            }
        }

        if (uniqueKeys.size === 0) return;

        const keys = Array.from(uniqueKeys.keys());
        const obs = keys.map(key => {
            const { indexId, bateCode, wToVolume } = uniqueKeys.get(key);
            return this.plattsPricesService.fetchPriceAverage(indexId, bateCode, this.indexDateRange, { ...params, wToVolume });
        });

        forkJoin(obs).subscribe((data: any[]) => {
            const resultsByKey: Record<string, any> = {};
            keys.forEach((k, i) => resultsByKey[k] = data[i]);
            for (const { offer, key } of offerKeyMap) {
                const val = resultsByKey[key];
                offer.bid.index['average_price'] = (typeof val === 'number' && !isNaN(val)) ? val : 0;
            }
            this.recalculateAll();
            this.refreshTable();
            this.cdr.detectChanges();
        });
    }

    /**
     * Default the Index / Market toggles to ON when the loaded offers
     * actually carry index / market price data and the user has not
     * explicitly chosen otherwise. Persists the chosen default to
     * localStorage so subsequent loads are stable.
     */
    private applyIncludeDefaultsFromData() {
        if (!this.indexToggleHasUserChoice) {
            const hasIndexData = this.offers.some((o: any) => o?.bid?.index);
            if (hasIndexData) {
                this.includedIndex = true;
                this.setStorageItem('included_index', true);
                this.indexToggleHasUserChoice = true;
            }
        }
        if (!this.marketToggleHasUserChoice) {
            const hasMarketData = this.offers.some((o: any) => o?.bid?.market_price);
            if (hasMarketData) {
                this.includedMarket = true;
                this.setStorageItem('included_market', true);
                this.marketToggleHasUserChoice = true;
            }
        }
    }

    public recalculateAll(message = false) {
        this.updateOffersExtraData();
        this.assignLocationRankings();
        this.offers = this.sortOffersByLocationAndRanking(this.offers);
        this.isInit = false;
        if (message) {
            this.toast.success(`Unit conversion information has been updated`);
        }
    }

    /**
     * Assign each non-contract offer a 1-based rank within its location
     * group, sorted by total_price (the headline formula). Lower price
     * gets rank 1. AWARDED_CONTRACT rows are skipped — the contract is
     * the baseline, not a bid being ranked.
     */
    private assignLocationRankings() {
        const groups = new Map<string, any[]>();
        for (const o of this.offers as any[]) {
            const key = String(o?.contract?.location?.id || '');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(o);
        }
        for (const [, group] of groups) {
            const bids = group.filter(o =>
                (o as any)?.status !== 'AWARDED_CONTRACT'
                && o?.bid
            );
            bids.sort((a, b) => {
                const va = Number(a?.bid?.total_price);
                const vb = Number(b?.bid?.total_price);
                const sa = isFinite(va) && va !== 0 ? va : Infinity;
                const sb = isFinite(vb) && vb !== 0 ? vb : Infinity;
                return sa - sb;
            });
            bids.forEach((b, i) => {
                b.bid['location_ranking'] = i + 1;
            });
            // Clear ranking on contract rows so the column reads blank
            for (const o of group) {
                if ((o as any)?.status === 'AWARDED_CONTRACT' && o?.bid) {
                    o.bid['location_ranking'] = null;
                }
            }
        }
    }

    /**
     * Sort offers by IATA, contract-first, then ranking within each
     * location so bids appear cheapest-first beneath their current
     * contract.
     */
    private sortOffersByLocationAndRanking(offers: any[]): any[] {
        return [...offers].sort((a, b) => {
            const ia = a?.contract?.location?.iata || '';
            const ib = b?.contract?.location?.iata || '';
            if (ia < ib) return -1;
            if (ia > ib) return 1;

            const aw = a?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            const bw = b?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            if (aw !== bw) return aw - bw;

            const ra = Number(a?.bid?.location_ranking);
            const rb = Number(b?.bid?.location_ranking);
            const sa = isFinite(ra) && ra > 0 ? ra : 999;
            const sb = isFinite(rb) && rb > 0 ? rb : 999;
            return sa - sb;
        });
    }

    /**
     * Drop AWARDED_CONTRACT rows whose location does not appear in the
     * current tender's bid set. A "current contract" without a matching
     * bid in this tender is unrelated to the current tender and should
     * not show in the revision view.
     */
    private filterOrphanCurrentContracts(offers: any[]): any[] {
        const bidLocationIatas = new Set<string>();
        for (const o of offers) {
            if (o?.status !== 'AWARDED_CONTRACT') {
                const iata = o?.contract?.location?.iata;
                if (iata) bidLocationIatas.add(iata);
            }
        }
        return offers.filter((o) => {
            if (o?.status !== 'AWARDED_CONTRACT') return true;
            const iata = o?.contract?.location?.iata;
            return iata ? bidLocationIatas.has(iata) : false;
        });
    }

    /**
     * Sort offers so each location renders as a contiguous block, with the
     * current contract (AWARDED_CONTRACT) immediately above its bids:
     *   IATA asc → companyChild → companyInfo → contract-first → round asc.
     */
    private sortOffersByLocation(offers: any[]): any[] {
        return [...offers].sort((a, b) => {
            const ia = a?.contract?.location?.iata || '';
            const ib = b?.contract?.location?.iata || '';
            if (ia < ib) return -1;
            if (ia > ib) return 1;

            const ca = a?.contract?.['company_child']?.id ?? -1;
            const cb = b?.contract?.['company_child']?.id ?? -1;
            if (ca !== cb) return Number(ca) - Number(cb);

            const ka = (a?.contract?.['company_info'] || '').toString().toUpperCase();
            const kb = (b?.contract?.['company_info'] || '').toString().toUpperCase();
            if (ka < kb) return -1;
            if (ka > kb) return 1;

            // Within a location, current contract first.
            const aw = a?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            const bw = b?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            if (aw !== bw) return aw - bw;

            return Number(a?.round || 0) - Number(b?.round || 0);
        });
    }

    private updateOffersExtraData() {
        this.finalBaseAmount = 0;
        this.finalGap = 0;
        this.finalGapTotal = 0;
        this.currentContractsTotal = 0;
        this.totalVolume = 0;
        this.selectedContractsTotal = 0;
        this.selectedTotalVolume = 0;
        this.hasSelectedBids = false;
        const seenLocationsForVolume = new Set<string>();
        let awardedDiff = 0;
        let awardedPerFees = 0;
        let awardedAverageUplift = 0;
        let awardedFees = 0;
        let awardedMarket = 0;
        let awardedIndex = 0;
        let currentLocationKey: string = null;

        this.offers.forEach((offer, index) => {
            // Distinct contracts can share a location (different child company /
            // company info). The key must separate them, or seenLocationsForVolume
            // collapses them into one and their volume is counted once.
            const companyChildId = offer.contract['company_child'] ? offer.contract['company_child'].id : '-';
            const companyInfo = offer.contract['company_info']
                ? offer.contract['company_info'].replace(/\s/g, '').toUpperCase() : '-';
            const locationKey = `${offer.contract.location.id}_${companyChildId}_${companyInfo}`;

            if (locationKey != currentLocationKey) {
                currentLocationKey = locationKey;
                awardedDiff = 0;
                awardedPerFees = 0;
                awardedAverageUplift = 0;
                awardedFees = 0;
                awardedMarket = 0;
                awardedIndex = 0;
                offer['new_location'] = 'new_location';
            } else {
                offer['new_location'] = '';
            }

            const bid = offer.bid;
            const tables = this.tablesOf(bid);
            let marketValue = 0;
            let indexValue = 0;
            const averageUplift = offer.contract['average_uplift'] ? offer.contract['average_uplift'] : 0;
            const diffValue = this.toCurrencyToUnit(
                offer.bid.differential.price.currency.code,
                offer.bid.differential.units.code,
                offer.bid.differential.price.value,
                tables.jet
            );
            offer.bid['exchanged_differential'] = diffValue;
            // The SAF fee (the bid's saf_fee_amount_units) at the SAF fee rate, as bid_total._saf and
            // the classic VA: it enters the total and the formulas' saf_fee_amount.
            const safFeeValue = this.safFeeValueOf(offer.bid, tables.safFee);

            if (this.includedMarket) {
                if (offer.bid['market_price'] && offer.bid['market_price'].price) {
                    marketValue = this.toCurrencyToUnit(offer.bid['market_price'].price.price.currency.code, offer.bid['market_price'].price.units.code, offer.bid['market_price'].price.price.value, tables.jet);
                }
            }
            if (this.includedIndex && offer.bid.index) {
                indexValue = offer.bid.index['average_price'] ? parseFloat(offer.bid.index['average_price']) : 0;
            }

            offer.contract['dateStart'] = moment(offer.contract['date_start']);
            offer.contract['dateEnd'] = moment(offer.contract['date_end']);
            bid['fees_sum'] = Number(this.calcTotalFees(bid));
            bid['per_uplift_fees_total'] = Number(this.calcTotalPerFees(bid));

            const costOfCapital = this.conversionFactor?.costOfCapital || 0;
            // Terms are financed only from delivery, so transit time is deducted.
            // A prepayment bid has payment_terms = 0 and so earns no WACC credit.
            let paymentTerms = Number(bid?.payment?.open_invoice?.payment_terms) || 0;
            const transitTime = Number(bid['transit_time']) || 0;
            if (transitTime) {
                paymentTerms = Math.max(0, paymentTerms - transitTime);
            }
            const preWaccTotal = diffValue + bid['fees_sum'] + marketValue + indexValue + safFeeValue;
            let waccComputed = 0;
            if (costOfCapital && paymentTerms) {
                // Negative by construction: WACC is a credit. Longer payment
                // terms make a bid cheaper, not dearer.
                waccComputed = ((costOfCapital / 100) / 365) * paymentTerms * preWaccTotal * -1;
            }

            const formulaContext: Record<string, number> = {
                differential: diffValue || 0,
                revised_differential: 0,
                volume_fees_total: bid['fees_sum'] || 0,
                saf_fee_amount: safFeeValue || 0,
                per_uplift_fees_total: bid['per_uplift_fees_total'] || 0,
                index_value: indexValue || 0,
                market_price_value: marketValue || 0,
                weight_to_volume: formulaWeightToVolume(bid, tableUsgPerMt(this.defaultUnitRates), this.rateTables?.limits),
                wacc: parseFloat(waccComputed.toFixed(4)),
                payment_terms: paymentTerms,
                cost_of_capital: costOfCapital,
                average_uplift: averageUplift || 0,
                contract_volume: Number(offer.contract.volume) || 0,
                annual_frequency: Number(offer.contract['annual_frequency']) || 0,
                months_of_service: Number(offer.contract['month_service']) || 0,
                monthly_volume: Number(offer.contract['month_service'])
                    ? (Number(offer.contract.volume) || 0) / Number(offer.contract['month_service']) : 0,
                available_volume: Number(bid['fuel_availability_percentage']) || 0,
                saf_mandated_price: 0,
                saf_voluntary_price: 0,
                saf_blend_percentage: Number(bid['saf_mandate_blend']) || 0,
                volume_fees_all: 0,
                per_uplift_fees_all: 0,
                is_market_price: bid['market_price'] ? 1 : 0,
                volume_per_uplift: Number(offer.contract['annual_frequency'])
                    ? (Number(offer.contract.volume) || 0) / Number(offer.contract['annual_frequency']) : 0,
            };

            for (const [formulaKey, formulaConfig] of Object.entries(this.activeFormulas)) {
                try {
                    bid[formulaKey] = parseFloat(this.formulaParser.evaluate(formulaConfig.ast, formulaContext).toFixed(4));
                } catch (e) {
                    bid[formulaKey] = 0;
                }
            }

            // @ts-ignore
            if (offer.status === 'AWARDED_CONTRACT') {
                this.isPrevOffersExists = true;
                awardedDiff = diffValue;
                awardedMarket = marketValue;
                awardedIndex = indexValue;
                awardedPerFees = bid['per_uplift_fees_total'];
                awardedFees = bid['fees_sum'];
                if (averageUplift) {
                    awardedAverageUplift = averageUplift;
                } else {
                    try {
                        awardedAverageUplift = this.offers[index + 1].contract['average_uplift'];
                    } catch (e) {
                        awardedAverageUplift = averageUplift;
                    }
                }
                offer.contract['average_uplift'] = awardedAverageUplift;
            }

            // Per-row Contract Total: each row's own absolute spend at
            // this location's annual volume, in baseCurrency / baseUnit.
            const rowPerUnit = diffValue + marketValue + indexValue + bid['fees_sum'] +
                (bid['per_uplift_fees_total'] && averageUplift && !isNaN(averageUplift) && averageUplift !== 0
                    ? bid['per_uplift_fees_total'] / averageUplift : 0);
            const rowVolumeInBaseUnit = this.convertVolumeToBaseUnit(
                offer.contract.units?.code || this.baseUnit.code,
                Number(offer.contract.volume) || 0,
            );
            const rowContractTotal = rowVolumeInBaseUnit * rowPerUnit;
            bid['contract_total'] = rowContractTotal;

            // Aggregate annual tender volume once per physical location.
            // Use only location.id as the dedup key — a single airport
            // can have multiple offers (different suppliers / aliases /
            // companyChild values) but its annual volume should still
            // be counted once.
            const volumeDedupKey = String(offer?.contract?.location?.id || locationKey);
            if (!seenLocationsForVolume.has(volumeDedupKey)) {
                seenLocationsForVolume.add(volumeDedupKey);
                this.totalVolume += rowVolumeInBaseUnit;
            }

            // Aggregate selection-driven totals: if the user has ticked
            // this bid for the current model and entered an award %,
            // contribute its weighted spend / volume to the chips.
            // @ts-ignore - status union type
            if (offer.status !== 'AWARDED_CONTRACT' && offer['is_included']) {
                this.hasSelectedBids = true;
                const awardPct = Number(offer['input_awarded_percent']);
                if (isFinite(awardPct) && awardPct > 0) {
                    const factor = awardPct / 100;
                    this.selectedContractsTotal += rowContractTotal * factor;
                    this.selectedTotalVolume += rowVolumeInBaseUnit * factor;
                }
            }

            // Aggregate AWARDED_CONTRACT totals into the header chip so
            // users see baseline spend across the tender.
            // @ts-ignore - status comes back as the union type
            if (offer.status === 'AWARDED_CONTRACT') {
                this.currentContractsTotal += rowContractTotal;
            }
            // @ts-ignore
            if (offer.status !== 'AWARDED_CONTRACT') {
                const prev_sum = (awardedDiff + awardedMarket + awardedIndex + awardedFees + (awardedPerFees && awardedAverageUplift && !isNaN(awardedAverageUplift) && awardedAverageUplift !== 0
                        ? awardedPerFees / awardedAverageUplift : 0)) -
                    (diffValue + marketValue + indexValue + bid['fees_sum'] + (bid['per_uplift_fees_total'] && averageUplift && !isNaN(averageUplift) && averageUplift !== 0
                        ? bid['per_uplift_fees_total'] / averageUplift : 0));
                bid['prev_sum'] = prev_sum;

                // Gap Total = volume × per-unit gap. prev_sum is in
                // baseCurrency / baseUnit, so convert the contract's
                // yearly volume to baseUnit before multiplying. Zero
                // effect when contract.units == baseUnit.
                const volumeInBaseUnit = this.convertVolumeToBaseUnit(
                    offer.contract.units?.code || this.baseUnit.code,
                    Number(offer.contract.volume) || 0,
                );
                const totalGap = volumeInBaseUnit * prev_sum;
                bid['total_prev_sum'] = totalGap.toFixed(4);

                // Award Gap Total = Gap Total scaled by the supplier's
                // available volume (and by user-awarded % once chosen).
                // Treat missing/zero availability as 100% so the column
                // doesn't divide by zero / blow up.
                const availPctRaw = Number(offer.bid['fuel_availability_percentage']);
                const availPct = (isFinite(availPctRaw) && availPctRaw > 0) ? availPctRaw : 100;
                let awardGap = totalGap * (availPct / 100);
                if (offer['input_awarded_percent'] && offer['is_included']) {
                    awardGap = awardGap * (Number(offer['input_awarded_percent']) / 100);
                }
                bid['total_prev_sum2'] = awardGap.toFixed(4);
                if (offer['is_included']) {
                    this.finalBaseAmount += Number(bid['total_prev_sum2']);
                    this.finalGap += Number(bid['prev_sum']);
                    this.finalGapTotal += Number(bid['total_prev_sum']);
                }
            }
            if (this.isInit) {
                offer['input_awarded_percent'] = null;
                if (this.selectedModelTab && this.selectedModelTab['offers_awarded']) {
                    const savedInputAwardedPercent = this.selectedModelTab.offers_awarded.find(item => item.id == offer.id);
                    if (savedInputAwardedPercent) {
                        offer['input_awarded_percent'] = savedInputAwardedPercent['percent'];
                        // @ts-ignore
                        this.selected.push(offer.id);
                        offer['is_included'] = true;
                    } else {
                        offer['is_included'] = false;
                    }
                }
            }
        });
    }

    private updateIndexAveragePrices() {
        const params = {
            'currencyId': this.baseCurrency.id,
            'unitId': this.baseUnit.id,
            'dateRange': this.indexDateRange,
            'startDate': this.indexDateStart,
            'endDate': this.indexDateEnd,
            'tenderId': this.tender.id,
        };
        const averagePricesObs = [];

        this.offers.forEach((offer) => {
            if (offer.bid && offer.bid.index &&
                (offer.bid.index.index_code.provider == 'platts' || offer.bid.index.index_code.provider == 'argus' || offer.bid.index.index_code.provider == 'opis')
            ) {
                averagePricesObs.push(this.updateIndexAveragePrice(offer, params));
            }
        });

        forkJoin(averagePricesObs).subscribe();
    }

    public updateIndexAveragePrice(offer, params) {
        const defaultBateId = 2;
        return this.plattsPricesService.fetchPriceAverage(offer.bid.index.index_code.id,
            offer.bid.index.bate ? offer.bid.index.bate.id : defaultBateId, this.indexDateRange,
            { ...params, wToVolume: indexRateKey(offer.bid) })
            .pipe(map((data) => {
                offer.bid.index['average_price'] = data;
            }));
    }

    public onDateRange(event) {
        this.indexDateRange = event.code;
        this.indexDateStart = event.dateStart;
        this.indexDateEnd = event.dateEnd;
        this.updateIndexAveragePrices();
    }

    private convertToUnit(unitCode: string, value: number, table: any = this.customUnits) {
        return UnitHelper.convertToUnit(unitCode, value, table, this.baseUnit.code);
    }

    /**
     * Convert a volume from `fromUnit` into the active baseUnit using the
     * customUnits rate table directly. Backend stores the rate table as
     * customUnits[from][to] = "1 from = rate to", so the correct
     * conversion is `value * rate` (UnitHelper.convertToUnit divides,
     * which is the inverse — that's the right shape for currency rates
     * but the wrong shape for unit rates from /system/unit-rates/).
     */
    private convertVolumeToBaseUnit(fromUnit: string, value: number): number {
        if (!fromUnit || !this.baseUnit?.code || fromUnit === this.baseUnit.code) {
            return value;
        }
        const rate = this.customUnits?.[fromUnit]?.[this.baseUnit.code];
        if (!rate || isNaN(rate)) return value;
        return value * Number(rate);
    }

    private convertToCurrency(currency: string, value: number) {
        return CurrencyHelper.convertToCurrency(currency, value, this.currencies, this.baseCurrency.code);
    }

    private toCurrencyToUnit(currency: string, unit: string, value: number, table: any = this.customUnits) {
        let val = this.convertToCurrency(currency, value);
        val = this.convertToUnit(unit, val, table);
        return val;
    }

    public updateExchangeRates(event) {
        this.getCurrencyRates().subscribe(() => {
            this.recalculateAll();
            this.refreshTable();
            this.toast.success(`Currency exchange conversion rates have been updated`);
        });
    }

    public onIsIncludedToReport(event) {
    }

    public onIsIncludedToReport2(isChecked, offer) {
        const offerId = offer.id;
        if (isChecked) {
            // @ts-ignore
            this.selected.push(offerId);
            offer['input_awarded_percent'] = 100;
            this.service.onUpdateOfferTabModels.emit({ offerId: offerId, value: 100 });
        } else {
            // @ts-ignore
            const idx = this.selected.indexOf(offerId);
            if (idx > -1) {
                this.selected.splice(idx, 1);
                offer['input_awarded_percent'] = null;
                this.service.onRemoveOfferTabModels.emit(offerId);
            }
        }
    }

    public getCurrencyRates() {
        return this.currencyRatesService.fetch(this.baseCurrency.code, {
            tender: this.tender.id,
        }, false)
            .pipe(map((currencies: []) => {
                this.currencies = currencies;
            }));
    }

    public setOffersCurrencies() {
        this.offers.forEach(offer => {
            // Touch nested codes; existing logic — kept for parity. No-op assignments removed.
            try { void offer.bid.differential.price.currency.code; } catch {}
            try { void offer.bid.index.index_code.currency; } catch {}
            try { void offer.bid['market_price']?.price?.currency?.code; } catch {}
            try { offer.bid.volume_based_fees.forEach(fee => { void fee.price.price.currency.code; }); } catch {}
        });
    }

    public setUnitsDefault($event) {
        this.setCustomUnits();
        this.recalculateAll(true);
        this.refreshTable();
    }

    public setCustomUnits() {
        this.customUnits = Object.assign({}, this.defaultUnitRates);
    }

    public calcTotalFees(bid) {
        let val = 0;
        const table = this.tablesOf(bid).jet;
        bid.volume_based_fees.forEach((fee, index) => {
            if (fee.price && fee.price.price) {
                const currency = fee.price.price.currency.code;
                const unit = fee.price.units.code;
                let value = fee.price.price.value;

                if (value) {
                    value = this.convertToCurrency(currency, value);
                    value = this.convertToUnit(unit, value, table);
                    val += value;
                    bid['exchanged_fee_' + (index)] = value;
                } else {
                    bid['exchanged_fee_' + (index)] = '';
                }
            }
        });
        return Number(val.toFixed(4));
    }

    public calcTotalPerFees(bid) {
        let val = 0;
        bid.per_usage_fees.forEach((fee, index) => {
            if (fee.price) {
                const currency = fee.price.currency.code;
                let value = fee.price.value;
                value = this.convertToCurrency(currency, value);
                if (!isNaN(value) && isFinite(value)) {
                    val += value;
                }
                bid['exchanged_per_fee_' + (index)] = (!isNaN(value) && isFinite(value) ? value : 0);
            }
        });
        return (isNaN(val) || !isFinite(val) ? 0 : val).toFixed(4);
    }

    public onSearch() {
        this.fetchOffers();
    }

    private fetchTenderCurrentContract() {
        if (this.tender.tenderCurrentContractId) {
            return this.service.fetchTenderCurrentContract(this.tender.tenderCurrentContractId)
                .pipe(map((tender: any) => {
                    this.previousTender = Tender.create(tender);
                }));
        }
        return null;
    }

    public baseUnitChange(value) {
        this.baseUnit = value;
        this.recalculateAll();
        this.refreshTable();
        this.setStorageItem('base_unit', value);
    }

    public baseCurrencyChange(value) {
        this.baseCurrency = value;
        this.getCurrencyRates().subscribe(() => {
            this.recalculateAll();
            this.refreshTable();
        });
        this.setStorageItem('base_curr', value);
    }

    public onCustomCurrency($event) {
        this.recalculateAll(true);
        this.refreshTable();
    }

    public onCustomUnit($event) {
        this.recalculateAll(true);
        this.refreshTable();
    }

    public onIncludedIndex(includedIndex) {
        this.includedIndex = includedIndex;
        this.setStorageItem('included_index', this.includedIndex);
        this.recalculateAll(true);
        this.refreshTable();
    }

    public onIncludedMarket(includedMarket) {
        this.includedMarket = includedMarket;
        this.setStorageItem('included_market', this.includedMarket);
        this.recalculateAll(true);
        this.refreshTable();
    }

    public setStorageItem(key: string, value: any) {
        localStorage.setItem(key, JSON.stringify(value));
    }

    public getStorageItem(key: string) {
        return JSON.parse(localStorage.getItem(key));
    }

    public removeStorageItem(key: string) {
        localStorage.removeItem(key);
    }

    public onSelectedModelTab(tab) {
        this.selectedModelTab = tab;
        this.isInit = true;
        this.recalculateAll();
        this.refreshTable();
    }

    public onChangedModelTabs(event) {
        this.recalculateAll();
        this.refreshTable();
    }

    public onAwardedPercent(event, offerId, value) {
        if (!value) {
            this.service.onRemoveOfferTabModels.emit(offerId);
        } else {
            this.service.onUpdateOfferTabModels.emit({ offerId: offerId, value: value });
        }
    }

    private setOrderedHeaders() {
        if (this.userSettings?.revis_col_order && this.userSettings.revis_col_order.length) {
            this.orderedHeadersArray = this.userSettings.revis_col_order;
            this.onSaveSorted(this.orderedHeadersArray);
        }
    }

    private setColumnHeaders() {
        this.headers.forEach(item => {
            this.colHeaders[item.id] = item.label;
        });
    }

    public onSaveHidden(event) {
        this.hiddenHeaders = event;
        this.buildColumnDefs();
        this.refreshTable();
    }

    public onSaveSorted(event) {
        this.orderedHeadersArray = event;
        this.orderedHeaders = {};
        event.forEach((item, index) => {
            this.orderedHeaders[item] = index;
        });
        this.buildColumnDefs();
        this.refreshTable();
    }

    public onConvertedUnits(event) {
        this.refreshTable();
    }

    // ─── TanStack Table ────────────────────────────────────────────────

    private buildColumnDefs() {
        const visible = this.headers.filter(h => !this.hiddenHeaders.includes(h.id));

        const frozenSet = new Set(this.frozenColumns);
        const frozenList = FREEZABLE_COLUMN_ORDER
            .filter(id => frozenSet.has(id))
            .map(id => visible.find(h => h.id === id))
            .filter((h): h is HeaderDef => !!h);

        const nonFrozen = visible
            .filter(h => !frozenSet.has(h.id))
            .sort((a, b) => {
                const ai = this.orderedHeaders[a.id];
                const bi = this.orderedHeaders[b.id];
                if (ai !== undefined && bi !== undefined) return ai - bi;
                if (ai !== undefined) return -1;
                if (bi !== undefined) return 1;
                return a.index - b.index;
            });

        this.columnDefs = [...frozenList, ...nonFrozen].map(h => this.toColumnDef(h));
    }

    public isFrozen(columnId: string): boolean {
        return this.frozenColumns.includes(columnId) && !this.hiddenHeaders.includes(columnId);
    }

    public getFrozenLeft(columnId: string): number {
        if (!this.isFrozen(columnId)) return 0;
        let left = 0;
        for (const id of FREEZABLE_COLUMN_ORDER) {
            if (id === columnId) return left;
            if (this.frozenColumns.includes(id) && !this.hiddenHeaders.includes(id) && !this.settingsHiddenColumnIds.has(id)) {
                left += this.columnWidths[id] || 120;
            }
        }
        return left;
    }

    public getColumnWidth(columnId: string): number | null {
        return this.columnWidths[columnId] || null;
    }

    public get freezableHeaders(): HeaderDef[] {
        // Preserve modal-row order — only emit headers that are freezable AND visible in this tender.
        return this.headers.filter(h => h.freezable);
    }

    public onSaveFrozen(frozen: string[]) {
        this.frozenColumns = (frozen || []).filter(id => !this.settingsHiddenColumnIds.has(id));
        this.buildColumnDefs();
        this.refreshTable();
    }

    /** Names of every volume-based fee on the bid, for the "All Fees" column. */
    public allFeeNames(offer: Offer): string {
        return (offer?.bid?.['volume_based_fees'] || [])
            .map((fee: any) => fee?.name)
            .filter(Boolean)
            .join(', ');
    }

    private toColumnDef(h: HeaderDef): ColumnDef<Offer> {
        const accessors: Record<string, (o: Offer) => any> = {
            iata: (o) => o.contract.location.iata,
            location_company: (o) => o.contract['company_child']?.alias || o.contract['company_child']?.name || o.contract['company_info'] || '',
            contract_period: (o) => o.contract['date_start'],
            yearly_volume: (o) => Number(o.contract.volume) || 0,
            monthly_volume: (o) => o.contract['month_service'] && +o.contract['month_service'] > 0 && o.contract.volume != null
                ? +o.contract.volume / +o.contract['month_service'] : 0,
            months_of_service: (o) => o.contract['month_service'],
            fuel_type: (o) => o.contract['fuel_type']?.name || '',
            flight_type: (o) => o.contract['flight_type']?.name || '',
            delivery_point: (o) => {
                const fp = o.contract['fuel_preference'] || (o.bid as any)?.fuelPreference;
                return fp?.name || '';
            },
            supplier: (o) => o.supplier?.name || '',
            alias: (o) => o.supplier?.['supplier_alias'] || '',
            round: (o) => o['round'],
            location_ranking: (o) => Number(o?.bid?.['location_ranking']) || null,
            offer: (o) => o['name'] || '',
            // @ts-ignore - status has stricter type but template accepts AWARDED_CONTRACT
            status: (o) => (o.status as any) === 'AWARDED_CONTRACT' ? 'Contract' : 'Initial Offer',
            intoplane: (o) => o.bid['into_plane'] || '',
            fuel_availability: (o) => o.bid['fuel_availability_percentage'] || 0,
            differential: (o) => o.bid['exchanged_differential'] ?? 0,
            // Sort on what the cell actually shows: converted rows all read
            // base currency/unit, un-converted rows read the bid's own.
            diff_currency_units: (o) => this.isConvertedUnits
                ? `${this.baseCurrency?.code} / ${this.baseUnit?.code}`
                : `${o.bid?.differential?.price?.currency?.code || ''} / ${o.bid?.differential?.units?.code || ''}`,
            base: (o) => o.bid?.index?.['index_code']
                ? this.tender.company.settings.getFilteredOptionLabel('index', o.bid.index['index_code'], 'code')
                : (o.bid['market_price']?.['source_name'] || ''),
            bate: (o) => o.bid?.index?.bate?.['name'] || '',
            averaging_method: (o) => o.bid?.index?.['averaging_method']?.name || '',
            averaging_offset: (o) => o.bid?.index?.['averaging_offset']?.name || '',
            update: (o) => o.bid?.index?.['effective_day']?.name || '',
            index_market_value: (o) => o.bid?.index?.['average_price'] || 0,
            index_market_unit: (o) => o.bid?.index?.['average_price']
                ? (this.baseUnit?.code || '')
                : (o.bid['market_price']?.price?.units?.code || ''),
            index_market_currency: (o) => o.bid?.index?.['average_price']
                ? (this.baseCurrency?.code || '')
                : (o.bid['market_price']?.price?.price?.currency?.code || ''),
            per_usage_based_fees: (o) => o.bid['per_uplift_fees_total'] || 0,
            av_uplift: (o) => o.contract['average_uplift'] || 0,
            total_fees: (o) => o.bid['fees_sum'] || 0,
            gap_vs_prev: (o) => Number(o.bid['prev_sum']) || 0,
            gap_total: (o) => Number(o.bid['total_prev_sum']) || 0,
            contract_total: (o) => o.bid['contract_total'] != null ? Number(o.bid['contract_total']) : null,
            award_gap_total: (o) => Number(o.bid['total_prev_sum2']) || 0,
        };

        // Fee columns 1..12 with price/unit
        for (let i = 1; i <= 12; i++) {
            const idx = i - 1;
            accessors[`fee_${i}`] = (o) => o.bid?.['volume_based_fees']?.[idx]?.name || '';
            accessors[`fee_${i}_price`] = (o) => o.bid?.['volume_based_fees']?.[idx] ? (o.bid['exchanged_fee_' + idx] ?? 0) : '';
            accessors[`fee_${i}_unit`] = (o) => o.bid?.['volume_based_fees']?.[idx] ? '' : '';
        }
        // Keyed by header id, not formId — the "All Fees" header is
        // { formId: 'fees_1_12', id: 'all_fees' }, and toColumnDef() below
        // looks the accessor up by id. Shares allFeeNames() with the template
        // so the sort key cannot drift from the text on screen.
        accessors['all_fees'] = (o) => this.allFeeNames(o);

        // Formula columns — values are computed during recalc and stored on
        // bid[formulaKey]. Provide a default numeric accessor for any
        // formula key without an explicit static accessor.
        for (const formulaKey of Object.keys(this.activeFormulas)) {
            if (!accessors[formulaKey]) {
                accessors[formulaKey] = (o) => Number(o.bid?.[formulaKey]) || 0;
            }
        }

        // Compound sort: keep location grouping (IATA → contract-first)
        // as the primary key, then apply this column's value as a tie-break.
        // This way clicking any column header sorts WITHIN each location
        // block instead of dispersing the contract row away from its bids.
        const compoundSortingFn = (rowA: any, rowB: any, columnId: string) => {
            const a = rowA.original;
            const b = rowB.original;
            const ia = a?.contract?.location?.iata || '';
            const ib = b?.contract?.location?.iata || '';
            if (ia < ib) return -1;
            if (ia > ib) return 1;

            const aw = a?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            const bw = b?.status === 'AWARDED_CONTRACT' ? 0 : 1;
            if (aw !== bw) return aw - bw;

            const va = rowA.getValue(columnId);
            const vb = rowB.getValue(columnId);
            if (va === vb) return 0;
            if (va === null || va === undefined) return 1;
            if (vb === null || vb === undefined) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return va - vb;
            return String(va).localeCompare(String(vb));
        };

        const def: ColumnDef<Offer> = {
            id: h.id,
            header: h.label,
            accessorFn: accessors[h.id] || ((o: Offer) => (o as any)[h.id]),
            enableSorting: !!h.order,
            sortingFn: compoundSortingFn,
        };
        return def;
    }

    private createTable() {
        this.table = createAngularTable<Offer>(() => ({
            data: this.offers,
            columns: this.columnDefs,
            state: {
                sorting: this.sorting,
            },
            initialState: {
                pagination: { pageIndex: 0, pageSize: 25 },
            },
            onSortingChange: (updater) => {
                const next = typeof updater === 'function' ? updater(this.sorting) : updater;
                this.sorting = next;
                this.persistSort();
                this.refreshTable();
            },
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            autoResetPageIndex: false,
        }));
    }

    private refreshTable() {
        if (!this.table) return;
        this.table.setOptions(prev => ({
            ...prev,
            data: [...this.offers],
            columns: this.columnDefs,
            state: {
                ...prev.state,
                sorting: this.sorting,
            },
        }));
        this.cdr.detectChanges();
    }

    private persistSort() {
        this.userSettingService.update({
            'revis_col_sort': this.sorting,
        }).subscribe();
    }

    public setPageSize(size: number) {
        if (!this.table) return;
        this.table.setPageSize(size);
        this.cdr.detectChanges();
    }

    public goToPage(index: number) {
        if (!this.table) return;
        this.table.setPageIndex(index);
        this.cdr.detectChanges();
    }

    public nextPage() {
        if (!this.table) return;
        this.table.nextPage();
        this.cdr.detectChanges();
    }

    public prevPage() {
        if (!this.table) return;
        this.table.previousPage();
        this.cdr.detectChanges();
    }

    public get pageStart(): number {
        if (!this.table) return 0;
        const s = this.table.getState();
        return s.pagination.pageIndex * s.pagination.pageSize + 1;
    }

    public get pageEnd(): number {
        if (!this.table) return 0;
        const s = this.table.getState();
        return Math.min((s.pagination.pageIndex + 1) * s.pagination.pageSize, this.totalRows);
    }

    public get totalRows(): number {
        return this.table?.getCoreRowModel().rows.length || 0;
    }

    public trackOffer(_idx: number, row: any): any {
        return row.original?.id || _idx;
    }

    public isNumeric(value: any): boolean {
        if (value === null || value === undefined || value === '') return false;
        const n = Number(value);
        return !isNaN(n) && isFinite(n);
    }

    public toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;
        // Lock the page scroll while the panel is fullscreen so the table's
        // own sticky header takes over.
        document.body.classList.toggle('jft-overview-fullscreen-lock', this.isFullscreen);
    }

    @HostListener('document:keydown.escape')
    public onEscape() {
        if (this.isFullscreen) {
            this.toggleFullscreen();
        }
    }
}
